import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';

import IconButton from 'components/common/IconButton';
import IconPlus from 'components/icons/IconPlus';

import NoteCategoriesTableEmpty from './NoteCategoriesTableEmpty';
import NoteCategoriesTableRow from './NoteCategoriesTableRow';

const styles = theme => ({
  table: {
    display: 'block',
    height: '100%',
  },
  tableBody: {
    display: 'block',
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 2px 10px 0 rgba(72,126,161,0.14)',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  cellIntake: {
    color: theme.palette.states.intake,
  },
  cellDelivery: {
    color: theme.palette.states.delivery,
  },
  cellBilling: {
    color: theme.palette.states.billing,
  },
  cellCollection: {
    color: theme.palette.states.collection,
  },
  cellPaid: {
    color: theme.palette.states.paid,
  },
  cellGeneral: {
    color: theme.palette.secondary[500],
  },
  cellHidden: {
    fontSize: 0,
  },
  cellButton: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'right',
    padding: 0,
    borderTopRightRadius: 10,
    '&:last-child': {
      padding: 0,
    },
  },
  gridItem: {
    maxWidth: '16.66%',
    flexBasis: '16.66%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '33.33%',
      flexBasis: '33.33%',
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '50%',
      flexBasis: '50%',
    },
  },
  iconButton: {
    color: 'inherit',
  },
  iconPlus: {
    width: 14,
    height: 14,
  },
  tableHead: {
    display: 'block',
    width: '100%',
    position: 'sticky',
    zIndex: 40,
    top: 207,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: -8,
      right: -8,
      bottom: 0,
      zIndex: -1,
      backgroundColor: theme.palette.grey[100],
    },
    [theme.breakpoints.down('sm')]: {
      top: 171,
      zIndex: 20,
      transition: theme.transitions.create(['top'], {
        duration: theme.transitions.duration.short,
      }),
    },
  },
  tableRow: {
    display: 'flex',
    width: '100%',
    flexWrap: 'nowrap',
  },
  cellName: {
    display: 'flex',
    maxWidth: '70%',
    flexBasis: '70%',
    paddingTop: 8,
    paddingBottom: 8,
    alignItems: 'center',
    borderTopLeftRadius: 10,
  },
});

const NoteCategoriesTable = ({
  onNoteCategoryDeleteFail,
  items,
  onCreate,
  classes,
  loading,
}) => {
  if (loading === 0 && items.length === 0) {
    return (<NoteCategoriesTableEmpty />);
  } else if (items.length === 0) {
    return '';
  }
  const intakeItems = items.filter(item => item['Workflow State'] && item['Workflow State'].ID && item['Workflow State'].ID === 1);
  const deliveryItems = items.filter(item => item['Workflow State'] && item['Workflow State'].ID && item['Workflow State'].ID === 2);
  const billingItems = items.filter(item => item['Workflow State'] && item['Workflow State'].ID && item['Workflow State'].ID === 3);
  const collectionItems = items.filter(item => item['Workflow State'] && item['Workflow State'].ID && item['Workflow State'].ID === 4);
  const paidItems = items.filter(item => item['Workflow State'] && item['Workflow State'].ID && item['Workflow State'].ID === 5);
  const generalItems = items.filter(item => !item['Workflow State'] || !item['Workflow State'].ID);
  return (
    <Grid
      container
      spacing={24}
    >
      <Grid
        item
        className={classes.gridItem}
      >
        <Table className={classes.table}>
          <TableHead
            className={classes.tableHead}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellIntake, classes.cellName)}>Intake</TableCell>
              <TableCell className={classNames(classes.cellIntake, classes.cellButton)} numeric>
                <IconButton
                  className={classes.iconButton}
                  color="primary"
                  onClick={() => onCreate('Intake')}
                  tooltipContent="Create Intake Note Category"
                >
                  <IconPlus className={classes.iconPlus} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {intakeItems.map(item => (
              <NoteCategoriesTableRow
                key={item.ID}
                onNoteCategoryDeleteFail={onNoteCategoryDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        item
        className={classes.gridItem}
      >
        <Table className={classes.table}>
          <TableHead
            className={classes.tableHead}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellDelivery, classes.cellName)}>Delivery</TableCell>
              <TableCell className={classNames(classes.cellDelivery, classes.cellButton)} numeric>
                <IconButton
                  className={classes.iconButton}
                  color="primary"
                  onClick={() => onCreate('Delivery')}
                  tooltipContent="Create Delivery Note Category"
                >
                  <IconPlus className={classes.iconPlus} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {deliveryItems.map(item => (
              <NoteCategoriesTableRow
                key={item.ID}
                onNoteCategoryDeleteFail={onNoteCategoryDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        item
        className={classes.gridItem}
      >
        <Table className={classes.table}>
          <TableHead
            className={classes.tableHead}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellBilling, classes.cellName)}>Billing</TableCell>
              <TableCell className={classNames(classes.cellBilling, classes.cellButton)} numeric>
                <IconButton
                  className={classes.iconButton}
                  color="primary"
                  onClick={() => onCreate('Billing')}
                  tooltipContent="Create Billing Note Category"
                >
                  <IconPlus className={classes.iconPlus} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {billingItems.map(item => (
              <NoteCategoriesTableRow
                key={item.ID}
                onNoteCategoryDeleteFail={onNoteCategoryDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        item
        className={classes.gridItem}
      >
        <Table className={classes.table}>
          <TableHead
            className={classes.tableHead}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellCollection, classes.cellName)}>Collection</TableCell>
              <TableCell className={classNames(classes.cellCollection, classes.cellButton)} numeric>
                <IconButton
                  className={classes.iconButton}
                  color="primary"
                  onClick={() => onCreate('Collection')}
                  tooltipContent="Create Collection Note Category"
                >
                  <IconPlus className={classes.iconPlus} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {collectionItems.map(item => (
              <NoteCategoriesTableRow
                key={item.ID}
                onNoteCategoryDeleteFail={onNoteCategoryDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        item
        className={classes.gridItem}
      >
        <Table className={classes.table}>
          <TableHead
            className={classes.tableHead}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellPaid, classes.cellName)}>Paid</TableCell>
              <TableCell className={classNames(classes.cellPaid, classes.cellButton)} numeric>
                <IconButton
                  className={classes.iconButton}
                  color="primary"
                  onClick={() => onCreate('Paid')}
                  tooltipContent="Create Paid Note Category"
                >
                  <IconPlus className={classes.iconPlus} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {paidItems.map(item => (
              <NoteCategoriesTableRow
                key={item.ID}
                onNoteCategoryDeleteFail={onNoteCategoryDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid
        item
        className={classes.gridItem}
      >
        <Table className={classes.table}>
          <TableHead
            className={classes.tableHead}
          >
            <TableRow className={classes.tableRow}>
              <TableCell className={classNames(classes.cellGeneral, classes.cellName)}>General</TableCell>
              <TableCell className={classNames(classes.cellGeneral, classes.cellButton)} numeric>
                <IconButton
                  className={classes.iconButton}
                  color="primary"
                  onClick={() => onCreate('General')}
                  tooltipContent="Create General Note Category"
                >
                  <IconPlus className={classes.iconPlus} />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {generalItems.map(item => (
              <NoteCategoriesTableRow
                key={item.ID}
                onNoteCategoryDeleteFail={onNoteCategoryDeleteFail}
                {...item}
              />
            ))}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};

NoteCategoriesTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  loading: PropTypes.number,
  onCreate: PropTypes.func.isRequired,
  onNoteCategoryDeleteFail: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    table: PropTypes.string.isRequired,
    cellIntake: PropTypes.string.isRequired,
  }).isRequired,
};

NoteCategoriesTable.defaultProps = {
  loading: 0,
};

export default withStyles(styles)(NoteCategoriesTable);



// WEBPACK FOOTER //
// ./src/components/settings/NoteCategoriesTable/NoteCategoriesTable.js